/* "Fix SQL" for a rule on one table, where the caller knows the rule and the table but not the run —
 * an applied recommendation on the Suggestions screen.
 *
 * **Looked up only when pressed**: the newest violation that rule recorded on that table, and the
 * panel for it. When there is none the button says so in words rather than opening an empty panel —
 * "nothing to fix yet" is the answer, and it is a different answer from a failure to look.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useLatestViolation } from '../api/rules';
import { ResolutionSqlPanel } from './ResolutionSqlPanel';

export function LatestViolationFix({
  ruleId,
  datasetId,
  className = 'lq-sect__ghost',
  label,
}: {
  readonly ruleId: string;
  readonly datasetId: string;
  readonly className?: string;
  /** The button's words, for a caller whose reader arrives asking about violations rather than about
   * SQL — the Score tab's "View violations and Fix SQL". The panel it opens is the same. */
  readonly label?: string | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const [asked, setAsked] = useState(false);
  const [open, setOpen] = useState(false);
  const latest = useLatestViolation(ruleId, datasetId, asked);

  const found = latest.isSuccess ? latest.data : undefined;
  return (
    <>
      <button
        type="button"
        className={className}
        disabled={asked && latest.isFetching}
        title={t('resolution.button.hint')}
        onClick={() => {
          setAsked(true);
          setOpen(true);
        }}
      >
        {label ?? t('resolution.button.label')}
      </button>
      {asked && latest.isError && (
        <span className="lq-rs__inline" role="alert">
          {latest.error.message}
        </span>
      )}
      {asked && found === null && (
        <span className="lq-rs__inline" role="status">
          {t('resolution.button.none')}
        </span>
      )}
      <ResolutionSqlPanel
        violationId={open && found !== undefined && found !== null ? found.id : undefined}
        onClose={() => {
          setOpen(false);
        }}
      />
    </>
  );
}
